import type { Config } from "tailwindcss";
import plugin from "tailwindcss/plugin";
import TailwindCssFormsPlugin from "@tailwindcss/forms";

const customColors = {
  cerulean: {
    DEFAULT: "#F6F7FA",
    100: "#F6F7FA",
  },
  primary: {
    DEFAULT: "#5881C1",
    100: "#F9FAFD",
    200: "#F2F5FA",
    300: "#E4EBF5",
    400: "#D7E1F0",
    500: "#BCCDE6",
    600: "#A1B8DC",
    700: "#94AFD8",
    800: "#799ACD",
    900: "#5881C1",
  },
  secondary: {
    DEFAULT: "#183E7A",
    100: "#183E7A04",
    200: "#183E7A8",
    300: "#183E7A16",
    400: "#183E7A24",
    500: "#183E7A40",
    600: "#183E7A56",
    700: "#183E7A64",
    800: "#183E7A80",
    900: "#183E7A",
  },
  tertiary: {
    DEFAULT: "#ADB5BD",
    100: "#F8F9FA",
    200: "#E9ECEF",
    300: "#DEE2E6",
    400: "#CED4DA",
    500: "#ADB5BD",
    600: "#6C757D",
    700: "#495057",
    800: "#343A40",
    900: "#212529",
  },
  error: {
    DEFAULT: "#FF3932",
    100: "#FFE4E4",
    200: "#FFBFBF",
    300: "#FF8F8F",
    400: "#FF6A6A",
    500: "#EB5757",
    600: "#CC3F3F",
    700: "#AA2F2F",
    800: "#88201F",
    900: "#661010",
  },
  danger: "#EF4444",
  warning: {
    DEFAULT: "#F8AE53",
    100: "#FFF5EB",
    200: "#FFEDD6",
    300: "#FFE1B2",
    400: "#FFD48E",
    500: "#F8AE53",
    600: "#E99D3E",
    700: "#D98C2A",
    800: "#CA7B15",
  },
  forest: {
    DEFAULT: "#84BCAF",
    100: "#E7F4F1",
    200: "#C0E7DD",
    300: "#99D9C9",
    400: "#4DC1A2",
    500: "#84BCAF",
    600: "#6FAEA3",
    700: "#5AA098",
    800: "#45928C",
  },
  success: {
    DEFAULT: "#60B527",
    100: "#E8F8E6",
    200: "#CFF1CD",
    300: "#B6EAB4",
    400: "#8EE08A",
    500: "#60B527",
    600: "#4EA41F",
    700: "#3D9317",
    800: "#2B8110",
    900: "#1A7008",
  },
  "new-purple": "#89509B",
  "light-purple": "#F6F1F7",
  table: {
    rowBorder: "#EFF3F9",
  },
};

const config: Config = {
  content: [
    "./src/routes/**/*.tsx",
    "./src/lib/components/**/*.tsx",
    "./src/features/**/*.tsx",
  ],
  theme: {
    extend: {
      fontFamily: {
        barlow: ["Barlow", "sans-serif"],
      },
      colors: customColors,
      translate: {
        "3d": "translate3d(0,0,0)",
      },
      boxShadow: {
        displayBoxShadow: "0px 0px 4px 0px #B0ADADBF",
        displayBoxShadow2: "0px 0px 3px 0px #B0ADAD59",
        dragButtonShadow: "0px 0px 3px 0px #D9D9D9",
        widgetImgShadow: "0px 0px 1.51px 0px #B0ADADBF",
        lotProductBoxShadow: "0px 0px 4px 0px #ADB5BD59",
        filtersBoxShadow: "0px 0px 8px 0px #0000000D",
      },
      keyframes: {
        popup: {
          "0%": {
            opacity: "0",
            scale: "0.7",
          },
          "100%": {
            opacity: "1",
            scale: "1",
          },
        },
        shimmer: {
          "0%": {
            transform: "translateX(-100%)",
          },
          "100%": {
            transform: "translateX(100%)",
          },
        },
      },
      animation: {
        popup: "popup 0.3s ease-in-out",
        shimmer: "shimmer 2s infinite",
      },
      borderRadius: {
        pill: "100vh",
        circle: "50%",
      },
    },
  },
  plugins: [
    TailwindCssFormsPlugin,
    plugin(({ addVariant, addUtilities }) => {
      addVariant("aria-invalid", '&[aria-invalid="true"]');
      addUtilities({
        ".overflow-unset": {
          overflow: "unset",
        },
      });
    }),
  ],
};

export { customColors };

export default config;
